/**
 * PauseMenu - Minecraft-style pause menu with options panel
 *
 * Appears when player presses Escape and no other UI is open.
 * Provides: Continue, Options, Save World, Exit to Title.
 *
 * Options panel: Render Distance, Volume, Sensitivity sliders.
 * Settings persisted in localStorage key 'voxelgame_settings'.
 */

const SETTINGS_KEY = 'voxelgame_settings';

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  return null;
}

function saveSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (_) { /* ignore */ }
}

export class PauseMenu {
  /**
   * @param {{ onSave: Function, onResume: Function, onRenderDistance: Function, onVolume: Function, onSensitivity: Function, onGameMode: Function }} callbacks
   */
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.isOpen = false;
    this._showingOptions = false;
    this._saveMsg = null;
    this._saveMsgTimer = 0;

    // Load persisted settings (or defaults)
    const saved = loadSettings() || {};
    this.settings = {
      renderDistance: saved.renderDistance ?? 6,
      volume: saved.volume ?? 100,
      sensitivity: saved.sensitivity ?? 1.0,
      /* Resolución: cuántos píxeles se dibujan por cada píxel de ventana.
         
         Antes se usaba window.devicePixelRatio a pelo. En un portátil Retina
         eso da 2, y el lienzo salía a 2560x1440 —3,7 megapíxeles por cuadro—
         que se pagan enteros en relleno. Medido en un M3 con la ventana a
         1280x720.
         
         Se topa en 1,5: baja el relleno un 44% frente a 2, y con texturas de
         píxel y filtro nearest la diferencia casi no se ve. El deslizador va
         de 0,5 —para portátiles flojos— a 2. */
      resolucion: saved.resolucion ?? Math.min(window.devicePixelRatio || 1, 1.5),
      /* El brillo (bloom) cuesta de verdad: medido en un M3, 8,6 -> 11,3 ms por
         cuadro, de 116 a 88 fps. Merece la pena porque es lo que hace que las
         antorchas y el botín legendario desborden luz, pero en un portátil
         flojo tiene que poder apagarse. */
      brillo: saved.brillo ?? true,
    };

    // Apply saved settings on construction
    if (callbacks.onRenderDistance) callbacks.onRenderDistance(this.settings.renderDistance);
    if (callbacks.onResolucion) callbacks.onResolucion(this.settings.resolucion);
    if (callbacks.onBrillo) callbacks.onBrillo(this.settings.brillo);
    if (callbacks.onVolume) callbacks.onVolume(this.settings.volume / 100);
    if (callbacks.onSensitivity) callbacks.onSensitivity(this.settings.sensitivity);

    // --- Build DOM ---
    this._buildOverlay();
    this._buildMainMenu();
    this._buildOptionsPanel();

    this.overlay.appendChild(this.mainMenu);
    this.overlay.appendChild(this.optionsPanel);
    document.body.appendChild(this.overlay);
  }

  // ================================================================
  // DOM construction
  // ================================================================

  _buildOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'pause-menu-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.65);
      z-index: 900;
      display: none;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      cursor: default;
    `;
  }

  _buildMainMenu() {
    this.mainMenu = document.createElement('div');
    this.mainMenu.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    `;

    // Title
    const title = document.createElement('div');
    title.textContent = 'JUEGO EN PAUSA';
    title.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 28px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 3px 3px 0 #3F3F3F, -1px -1px 0 #3F3F3F;
      margin-bottom: 16px;
      letter-spacing: 3px;
      image-rendering: pixelated;
    `;
    this.mainMenu.appendChild(title);

    // Buttons
    this._btnContinue = this._createButton('Continuar', () => this._onContinue());
    this._btnOptions = this._createButton('Opciones', () => this._showOptions());
    /* Elegir skin estaba solo detrás del comando «skin» de la terminal, que
       Fede no va a descubrir jamás. Va aquí, con el resto. */
    this._btnSkin = this._createButton('Elegir Skin', () => this._onSkin());
    this._btnCompartir = this._createButton('Compartir Mundo', () => this._onCompartir());
    this._btnMultiplayer = this._createButton('Multijugador', () => this._onMultiplayer());
    this._btnGameMode = this._createButton('Cambiar Modo', () => this._onGameMode());
    this._btnSave = this._createButton('Guardar Mundo', () => this._onSave());
    this._btnExit = this._createButton('Salir al Titulo', () => this._onExit());

    this.mainMenu.appendChild(this._btnContinue);
    this.mainMenu.appendChild(this._btnOptions);
    this.mainMenu.appendChild(this._btnSkin);
    this.mainMenu.appendChild(this._btnCompartir);
    this.mainMenu.appendChild(this._btnMultiplayer);
    this.mainMenu.appendChild(this._btnGameMode);
    this.mainMenu.appendChild(this._btnSave);
    this.mainMenu.appendChild(this._btnExit);

    // Save confirmation message
    this._saveMsgEl = document.createElement('div');
    this._saveMsgEl.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 16px;
      color: #55FF55;
      text-shadow: 1px 1px 0 #003300;
      margin-top: 4px;
      height: 20px;
      text-align: center;
    `;
    this.mainMenu.appendChild(this._saveMsgEl);
  }

  _buildOptionsPanel() {
    this.optionsPanel = document.createElement('div');
    this.optionsPanel.style.cssText = `
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      max-width: 420px;
      width: 100%;
    `;

    // Options title
    const optTitle = document.createElement('div');
    optTitle.textContent = 'OPCIONES';
    optTitle.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 24px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 3px 3px 0 #3F3F3F;
      margin-bottom: 12px;
      letter-spacing: 2px;
    `;
    this.optionsPanel.appendChild(optTitle);

    // Render distance slider
    this._renderDistSlider = this._createSlider(
      'Distancia de Renderizado',
      2, 12, 1,
      this.settings.renderDistance,
      (val) => {
        this.settings.renderDistance = val;
        this._persistSettings();
        if (this.callbacks.onRenderDistance) this.callbacks.onRenderDistance(val);
      }
    );
    this.optionsPanel.appendChild(this._renderDistSlider.container);

    // Resolución
    this._resSlider = this._createSlider(
      'Resolucion',
      0.5, 2.0, 0.25,
      this.settings.resolucion,
      (val) => {
        this.settings.resolucion = parseFloat(val.toFixed(2));
        this._persistSettings();
        if (this.callbacks.onResolucion) this.callbacks.onResolucion(this.settings.resolucion);
      }
    );
    this.optionsPanel.appendChild(this._resSlider.container);

    // Brillo (bloom) — se puede apagar en máquinas justas
    this._btnBrillo = this._createButton(
      `Brillo: ${this.settings.brillo ? 'SI' : 'NO'}`,
      () => {
        this.settings.brillo = !this.settings.brillo;
        this._btnBrillo.textContent = `Brillo: ${this.settings.brillo ? 'SI' : 'NO'}`;
        this._persistSettings();
        if (this.callbacks.onBrillo) this.callbacks.onBrillo(this.settings.brillo);
      },
    );
    this.optionsPanel.appendChild(this._btnBrillo);

    // Volume slider
    this._volumeSlider = this._createSlider(
      'Volumen',
      0, 100, 5,
      this.settings.volume,
      (val) => {
        this.settings.volume = val;
        this._persistSettings();
        if (this.callbacks.onVolume) this.callbacks.onVolume(val / 100);
      }
    );
    this.optionsPanel.appendChild(this._volumeSlider.container);

    // Sensitivity slider
    this._sensitivitySlider = this._createSlider(
      'Sensibilidad',
      0.1, 3.0, 0.1,
      this.settings.sensitivity,
      (val) => {
        this.settings.sensitivity = parseFloat(val.toFixed(1));
        this._persistSettings();
        if (this.callbacks.onSensitivity) this.callbacks.onSensitivity(this.settings.sensitivity);
      }
    );
    this.optionsPanel.appendChild(this._sensitivitySlider.container);

    // Back button
    const btnBack = this._createButton('Atras', () => this._hideOptions());
    this.optionsPanel.appendChild(btnBack);
  }

  // ================================================================
  // Minecraft-style button factory
  // ================================================================

  _createButton(text, onClick) {
    const btn = document.createElement('div');
    btn.textContent = text;
    btn.style.cssText = `
      width: 280px;
      padding: 8px 0;
      text-align: center;
      font-family: 'Courier New', monospace;
      font-size: 16px;
      font-weight: bold;
      color: #E0E0E0;
      text-shadow: 2px 2px 0 #3F3F3F;
      background: #737373;
      border: 3px solid;
      border-top-color: #A0A0A0;
      border-left-color: #A0A0A0;
      border-bottom-color: #505050;
      border-right-color: #505050;
      cursor: pointer;
      user-select: none;
      image-rendering: pixelated;
      transition: none;
    `;

    btn.addEventListener('mouseenter', () => {
      btn.style.background = '#878787';
      btn.style.color = '#FFFFA0';
      btn.style.borderTopColor = '#B0B0B0';
      btn.style.borderLeftColor = '#B0B0B0';
      btn.style.borderBottomColor = '#606060';
      btn.style.borderRightColor = '#606060';
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#737373';
      btn.style.color = '#E0E0E0';
      btn.style.borderTopColor = '#A0A0A0';
      btn.style.borderLeftColor = '#A0A0A0';
      btn.style.borderBottomColor = '#505050';
      btn.style.borderRightColor = '#505050';
    });

    btn.addEventListener('mousedown', () => {
      btn.style.borderTopColor = '#505050';
      btn.style.borderLeftColor = '#505050';
      btn.style.borderBottomColor = '#A0A0A0';
      btn.style.borderRightColor = '#A0A0A0';
      btn.style.padding = '9px 0 7px 0';
    });

    btn.addEventListener('mouseup', () => {
      btn.style.borderTopColor = '#A0A0A0';
      btn.style.borderLeftColor = '#A0A0A0';
      btn.style.borderBottomColor = '#505050';
      btn.style.borderRightColor = '#505050';
      btn.style.padding = '8px 0';
    });

    btn.addEventListener('click', onClick);
    return btn;
  }

  // ================================================================
  // Minecraft-style slider factory
  // ================================================================

  _createSlider(label, min, max, step, initialValue, onChange) {
    const container = document.createElement('div');
    container.style.cssText = `
      width: 320px;
      margin-bottom: 4px;
    `;

    // Label + value display
    const labelRow = document.createElement('div');
    labelRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      color: #E0E0E0;
      text-shadow: 1px 1px 0 #3F3F3F;
      margin-bottom: 4px;
    `;

    const labelEl = document.createElement('span');
    labelEl.textContent = label;

    const valueEl = document.createElement('span');
    valueEl.textContent = step < 1 ? initialValue.toFixed(1) : initialValue;
    valueEl.style.color = '#FFFF55';

    labelRow.appendChild(labelEl);
    labelRow.appendChild(valueEl);
    container.appendChild(labelRow);

    // Slider track (Minecraft-style)
    const trackWrap = document.createElement('div');
    trackWrap.style.cssText = `
      position: relative;
      width: 100%;
      height: 24px;
      background: #000000;
      border: 2px solid;
      border-color: #373737 #FFFFFF #FFFFFF #373737;
      box-sizing: border-box;
    `;

    // Filled portion
    const fill = document.createElement('div');
    fill.style.cssText = `
      position: absolute;
      top: 0; left: 0; bottom: 0;
      background: #5A8A30;
      transition: width 0.05s;
    `;
    const fraction = (initialValue - min) / (max - min);
    fill.style.width = (fraction * 100) + '%';
    trackWrap.appendChild(fill);

    // Invisible range input on top for interaction
    const input = document.createElement('input');
    input.type = 'range';
    input.min = min;
    input.max = max;
    input.step = step;
    input.value = initialValue;
    input.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
      margin: 0;
    `;

    input.addEventListener('input', () => {
      const val = parseFloat(input.value);
      const frac = (val - min) / (max - min);
      fill.style.width = (frac * 100) + '%';
      valueEl.textContent = step < 1 ? val.toFixed(1) : val;
      onChange(val);
    });

    trackWrap.appendChild(input);
    container.appendChild(trackWrap);

    return { container, input, fill, valueEl };
  }

  // ================================================================
  // Actions
  // ================================================================

  _onContinue() {
    this.close();
    if (this.callbacks.onResume) this.callbacks.onResume();
  }

  _showOptions() {
    this._showingOptions = true;
    this.mainMenu.style.display = 'none';
    this.optionsPanel.style.display = 'flex';
  }

  _hideOptions() {
    this._showingOptions = false;
    this.optionsPanel.style.display = 'none';
    this.mainMenu.style.display = 'flex';
  }

  _onSkin() {
    if (this.callbacks.onSkin) this.callbacks.onSkin();
  }

  /* ==================================================================
     COMPARTIR EL MUNDO

     El mundo entero cabe en el enlace: no hay servidor, no hay cuenta y no
     caduca. Se enseña la URL en un campo seleccionable ADEMÁS de copiarla al
     portapapeles, porque el portapapeles falla —permisos, contexto no seguro,
     Safari— y si falla sin enseñar nada, Fede se queda sin su enlace.
     ================================================================== */
  async _onCompartir() {
    if (!this.callbacks.onCompartir) return;
    this._btnCompartir.textContent = 'Preparando...';
    let r;
    try {
      r = await this.callbacks.onCompartir();
    } catch (e) {
      this._btnCompartir.textContent = 'Compartir Mundo';
      this._mostrarCompartir(null, 'No se pudo preparar el enlace: ' + e.message);
      return;
    }
    this._btnCompartir.textContent = 'Compartir Mundo';
    let aviso = `${r.bloques} bloques · ${r.largo} caracteres`;
    if (r.largo > r.limite) {
      aviso += ' — es largo: puede que se corte en WhatsApp. Copialo entero.';
    }
    this._mostrarCompartir(r.url, aviso);
  }

  _mostrarCompartir(url, aviso) {
    if (!this._panelCompartir) {
      this._panelCompartir = document.createElement('div');
      this._panelCompartir.id = 'compartir-mundo';
      this._panelCompartir.style.cssText = `
        position:fixed; inset:0; background:rgba(0,0,0,0.82); z-index:960;
        display:none; justify-content:center; align-items:center;
        font-family:'Courier New',monospace;`;
      const caja = document.createElement('div');
      caja.style.cssText = `
        background:#2A2A2A; border:4px solid; border-color:#4A4A4A #1A1A1A #1A1A1A #4A4A4A;
        padding:22px 26px; width:min(620px,92vw); color:#DDD;
        display:flex; flex-direction:column; gap:12px;`;
      const t = document.createElement('div');
      t.textContent = 'Comparte tu mundo';
      t.style.cssText = 'font-size:20px;font-weight:bold;color:#FFD700;text-align:center;';
      caja.appendChild(t);

      const expl = document.createElement('div');
      expl.textContent = 'Quien abra este enlace vera tu mundo. No hace falta cuenta ni descargar nada.';
      expl.style.cssText = 'font-size:13px;line-height:1.5;';
      caja.appendChild(expl);

      this._campoUrl = document.createElement('input');
      this._campoUrl.readOnly = true;
      this._campoUrl.style.cssText = `
        width:100%; padding:10px; font-family:inherit; font-size:12px;
        background:#1A1A1A; color:#9EE89E; border:2px solid #555;`;
      this._campoUrl.addEventListener('click', () => this._campoUrl.select());
      caja.appendChild(this._campoUrl);

      this._avisoCompartir = document.createElement('div');
      this._avisoCompartir.style.cssText = 'font-size:12px;color:#BBB;min-height:16px;';
      caja.appendChild(this._avisoCompartir);

      const fila = document.createElement('div');
      fila.style.cssText = 'display:flex; gap:10px;';
      this._btnCopiar = document.createElement('button');
      this._btnCopiar.textContent = 'Copiar enlace';
      this._btnCopiar.style.cssText = `
        flex:1; padding:12px; cursor:pointer; font-family:inherit; font-weight:bold;
        background:#3A7A3A; border:2px solid; border-color:#5A9A5A #1A3A1A #1A3A1A #5A9A5A; color:#FFF;`;
      this._btnCopiar.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(this._campoUrl.value);
          this._btnCopiar.textContent = 'Copiado!';
        } catch {
          /* Sin permiso de portapapeles: se selecciona para copiar a mano. */
          this._campoUrl.select();
          this._btnCopiar.textContent = 'Copia con Ctrl+C';
        }
        setTimeout(() => { this._btnCopiar.textContent = 'Copiar enlace'; }, 2500);
      });
      fila.appendChild(this._btnCopiar);

      const cerrar = document.createElement('button');
      cerrar.textContent = 'Cerrar';
      cerrar.style.cssText = `
        flex:0 0 120px; padding:12px; cursor:pointer; font-family:inherit; font-weight:bold;
        background:#555; border:2px solid; border-color:#777 #333 #333 #777; color:#EEE;`;
      cerrar.addEventListener('click', () => { this._panelCompartir.style.display = 'none'; });
      fila.appendChild(cerrar);
      caja.appendChild(fila);

      this._panelCompartir.appendChild(caja);
      document.body.appendChild(this._panelCompartir);
    }
    this._campoUrl.value = url || '';
    this._campoUrl.style.display = url ? 'block' : 'none';
    this._btnCopiar.style.display = url ? 'block' : 'none';
    this._avisoCompartir.textContent = aviso || '';
    this._panelCompartir.style.display = 'flex';
  }

  _onMultiplayer() {
    if (this.callbacks.onMultiplayer) this.callbacks.onMultiplayer();
  }

  _onGameMode() {
    if (this.callbacks.onGameMode) this.callbacks.onGameMode();
  }

  _onSave() {
    if (this.callbacks.onSave) this.callbacks.onSave();
    this._saveMsgEl.textContent = 'Guardado!';
    // Clear after 2 seconds
    clearTimeout(this._saveMsgTimeout);
    this._saveMsgTimeout = setTimeout(() => {
      this._saveMsgEl.textContent = '';
    }, 2000);
  }

  _onExit() {
    window.location.reload();
  }

  _persistSettings() {
    saveSettings(this.settings);
  }

  // ================================================================
  // Public API
  // ================================================================

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this._showingOptions = false;
    this.mainMenu.style.display = 'flex';
    this.optionsPanel.style.display = 'none';
    this.overlay.style.display = 'flex';
    this._saveMsgEl.textContent = '';
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.style.display = 'none';
  }

  toggle() {
    if (this.isOpen) {
      if (this._showingOptions) {
        // If in options, go back to main pause menu first
        this._hideOptions();
        return;
      }
      this.close();
      if (this.callbacks.onResume) this.callbacks.onResume();
    } else {
      this.open();
    }
  }
}
